import React from 'react';

import { faInfoCircle } from '~components/icon';
import { Row } from '~components/table-view';
import HoverPopover from '~support-at-home/components/common/HoverPopover';

import { SahServiceInfo } from '~lookups/models';

// The pointer crosses this column on its way to the rest of the row, so the popover only opens
// once it has actually come to rest - otherwise a sweep across the table fires one per row.
const INFO_HOVER_DELAY_MS = 250;

interface ComponentProps {
  row: Row;
}

const yesNo = (value: boolean) => (value ? 'Yes' : 'No');

/**
 * The lines the popover shows for a service.
 *
 * Two groups. The first identifies the service to Services Australia - codes the app otherwise
 * never displays. The second is what actually bites later: these are the flags the supplier
 * invoice and claim item entry forms gate their fields on, so they decide what a user will be
 * asked for when coding against this service. A health professional type can only be picked when
 * the service has any; the wraparound/item choice only means something when both exist; and the
 * first-payment, prescribed-item and purchase-method questions appear only when their flag is set
 * (see forms/supplier-invoices/details.ts).
 *
 * Two periods of the same service can differ on any of these, and until now there was nowhere to
 * see that before coding an entry against one of them.
 */
const buildMessages = (info: SahServiceInfo): string[] =>
  [
    info.sah_id && `Service ID: ${info.sah_id}`,
    info.group && `Group: ${info.group}${info.group_id ? ` (${info.group_id})` : ''}`,
    info.service_type &&
      `Service type: ${info.service_type}${info.service_type_id ? ` (${info.service_type_id})` : ''}`,
    info.unit_type && `Unit type: ${info.unit_type}`,
    `Items: ${info.item_count ?? 0}`,
    `Wraparound services: ${info.wrap_count ?? 0}`,
    `Health professional types: ${info.hpt_count ?? 0}`,
    `First payment indicator required: ${yesNo(info.first_payment_indicator_required)}`,
    `Prescribed items allowed: ${yesNo(info.prescribed_items_allowed)}`,
    `Purchase method: ${
      info.purchase_method_allowed
        ? `Allowed${info.purchase_method_type ? ` (default ${info.purchase_method_type.toLowerCase()})` : ''}`
        : 'Not allowed'
    }`,
  ].filter(Boolean as unknown as (value: string | false | null | undefined) => value is string);

/**
 * The service name, with the department's reference and coding detail behind an info icon.
 *
 * The icon sits hard right in the cell rather than after the text, so it lands in the same place
 * on every row regardless of how long the name is or whether it wraps - a column of icons is
 * scannable, a ragged edge of them is not.
 */
// Called directly as `SahServiceCell({ row })` from list.ts's `renderReadOnly` (a .ts file, so no
// JSX is available at the call site), not rendered as `<SahServiceCell />` - typed as a plain
// function rather than `React.FC` so it reads as the render helper it is, not a component that
// could safely pick up a hook.
const SahServiceCell = ({ row }: ComponentProps) => {
  const info = row.service_info as SahServiceInfo | null | undefined;
  const messages = info ? buildMessages(info) : [];

  return (
    <div className="sah-service-cell">
      <span className="sah-service-cell-name">{row.name}</span>
      {messages.length > 0 && (
        <HoverPopover
          id={String(row.id)}
          icon={faInfoCircle}
          variant="info"
          triggerClassName="text-info"
          header="Service reference"
          messages={messages}
          ariaLabel={`Reference detail for ${row.name}`}
          dataCyPrefix="sah-service-info"
          placement="right"
          openDelay={INFO_HOVER_DELAY_MS}
        />
      )}
    </div>
  );
};

export default SahServiceCell;
